'use client';

import { useMemo } from 'react';

import {
  Root,
  TopBar,
  Container,
  Burger,
  Logo,
  Searchbar,
  AssistedSaleSearchbar,
  Actions,
  Navigation,
} from '@leroy-merlin-br/backpack-header';

import { type HeaderMenu } from '@/infra/services/categories/get-header-menu';
import { useAnalyticsTags } from '@/presenters/hooks/algolia/useAnalyticsTags';
import { useLoyaltyProfile, useUserResources } from '@/presenters/hooks/user';
import { useGlobalStore } from '@/presenters/store/global';
import { useServerContext } from '@/presenters/store/search/algolia-facets';
import {
  indexName,
  insightsConfig,
  searchClient,
  retailMediaIndexName,
} from '@/utils/algolia';

import {
  type HeaderBackpackMenus,
  locationContextAdapter,
  menuAdapter,
  userAdapter,
} from './adapters';
import { onUserRecontext, redirect } from './Header.utils';
import { locationRepository, navigationRepository } from './repositories';

export interface HeaderProps {
  menus: HeaderMenu[];
}

export default function Header(props: HeaderProps) {
  const { menus: menusFromProps } = props;

  const { isCategoryPage } = useServerContext();

  const analyticsTags = useAnalyticsTags(isCategoryPage);
  const session = useGlobalStore((state) => state.session);

  const { data: userResources } = useUserResources();
  const { data: loyaltyProfile } = useLoyaltyProfile();

  const isAssistedSale = userResources?.isAssistedSale ?? false;
  const theme =
    process.env.NEXT_PUBLIC_FEATURE_FLIP_BLACK_FRIDAY_ENABLED === 'true'
      ? 'black-weekend'
      : 'default';

  const menus: HeaderBackpackMenus = useMemo(() => {
    return menuAdapter(menusFromProps);
  }, [menusFromProps]);

  const user = useMemo(() => {
    return userAdapter({
      resources: userResources,
      loyaltyProfile,
    });
  }, [userResources, loyaltyProfile]);

  const locationContext = useMemo(() => {
    return locationContextAdapter(session);
  }, [session]);

  return (
    <Root
      data-testid="header"
      user={user}
      locationContext={locationContext}
      cartItems={userResources?.cartItems ?? 0}
      routing
      theme={theme}
    >
      <TopBar
        handleLocationRecontext={onUserRecontext}
        locationRepository={locationRepository}
      />

      <Container size="7xl">
        <Burger />
        <Logo />
        {isAssistedSale ? (
          <AssistedSaleSearchbar />
        ) : (
          <Searchbar
            algolia={{
              indexName: indexName,
              insightsClient: insightsConfig.insightsClient,
              searchClient,
              retailMediaIndexName,
              queries: [{ params: { analyticsTags }, indexName }],
            }}
          />
        )}

        <Actions
          providers={{
            disable: ['facebook'],
          }}
          onClickCart={() => redirect('/carrinho')}
        />
      </Container>

      <Navigation
        size="7xl"
        data={menus}
        navigationRepository={navigationRepository}
      />
    </Root>
  );
}
